import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateCompanyPolicyDTO,
  CreatePolicyTypeDTO,
  ListCompanyPoliciesQuery,
  ListPolicyVersionsQuery,
  UpdateCompanyPolicyDTO,
  UpdateCompanyPolicyStatusDTO,
  UpdatePolicyTypeDTO,
} from "@/modules/hr/company-policy/domain/types";
import { companyPolicyApi } from "./client";

const keys = {
  list: (q: ListCompanyPoliciesQuery) => ["hr", "company-policy", "list", q] as const,
  policyTypes: () => ["hr", "company-policy", "policy-types"] as const,
  detail: (id: string) => ["hr", "company-policy", "detail", id] as const,
  versions: (policyId: string, q: Omit<ListPolicyVersionsQuery, "policyId">) =>
    ["hr", "company-policy", "versions", policyId, q] as const,
};

export function useCompanyPoliciesQuery(query: ListCompanyPoliciesQuery) {
  return useQuery({
    queryKey: keys.list(query),
    queryFn: () => companyPolicyApi.list(query),
  });
}

export function usePolicyTypesQuery() {
  return useQuery({
    queryKey: keys.policyTypes(),
    queryFn: () => companyPolicyApi.listPolicyTypes(),
  });
}

export function useCreatePolicyTypeMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreatePolicyTypeDTO) => companyPolicyApi.createPolicyType(dto),
    onSuccess: () => {
      toast.success("ສ້າງປະເພດນະໂຍບາຍສຳເລັດ");
      qc.invalidateQueries({ queryKey: keys.policyTypes() });
      qc.invalidateQueries({ queryKey: ["hr", "company-policy"] });
    },
    onError: () => toast.error("ສ້າງປະເພດນະໂຍບາຍລົ້ມເຫຼວ"),
  });
}

export function useUpdatePolicyTypeMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdatePolicyTypeDTO }) =>
      companyPolicyApi.updatePolicyType(id, dto),
    onSuccess: (_, { id }) => {
      toast.success("ແກ້ໄຂປະເພດນະໂຍບາຍສຳເລັດ");
      qc.invalidateQueries({ queryKey: keys.policyTypes() });
      qc.invalidateQueries({ queryKey: ["hr", "company-policy"] });
    },
    onError: () => toast.error("ແກ້ໄຂປະເພດນະໂຍບາຍລົ້ມເຫຼວ"),
  });
}

export function useDeletePolicyTypeMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => companyPolicyApi.deletePolicyType(id),
    onSuccess: () => {
      toast.success("ລຶບປະເພດນະໂຍບາຍສຳເລັດ");
      qc.invalidateQueries({ queryKey: keys.policyTypes() });
      qc.invalidateQueries({ queryKey: ["hr", "company-policy"] });
    },
    onError: () => toast.error("ລຶບປະເພດນະໂຍບາຍລົ້ມເຫຼວ"),
  });
}

export function useCompanyPolicyDetailQuery(id: string | undefined) {
  return useQuery({
    queryKey: keys.detail(id ?? ""),
    queryFn: () => companyPolicyApi.getById(id!),
    enabled: !!id,
  });
}

export function useCreateCompanyPolicyMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateCompanyPolicyDTO) => companyPolicyApi.create(dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hr", "company-policy"] });
    },
  });
}

export function useUpdateCompanyPolicyMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateCompanyPolicyDTO }) =>
      companyPolicyApi.update(id, dto),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ["hr", "company-policy"] });
      qc.invalidateQueries({ queryKey: keys.detail(id) });
    },
  });
}

export function useUpdateCompanyPolicyStatusMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      dto,
    }: {
      id: string;
      dto: UpdateCompanyPolicyStatusDTO;
    }) => companyPolicyApi.updateStatus(id, dto),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ["hr", "company-policy"] });
      qc.invalidateQueries({ queryKey: keys.detail(id) });
    },
  });
}

export function useDeleteCompanyPolicyMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => companyPolicyApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hr", "company-policy"] });
    },
  });
}

export function usePolicyVersionsQuery(
  policyId: string | undefined,
  query: Omit<ListPolicyVersionsQuery, "policyId">,
) {
  return useQuery({
    queryKey: keys.versions(policyId ?? "", query),
    queryFn: () => companyPolicyApi.listVersions({ ...query, policyId: policyId! }),
    enabled: !!policyId,
  });
}
